import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaXmark } from 'react-icons/fa6';
import { NS } from '../../U3204';
import type { U3204ToolActivityItem } from '../../U3204Service';
import { u3204FormatToolLabel } from '../../utils/u3204ToolLabels';

interface U3204ToolActivityPanelProps {
  toolActivity: U3204ToolActivityItem[];
}

const U3204ToolActivityPanel: React.FC<U3204ToolActivityPanelProps> = ({ toolActivity }) => {
  const { t } = useTranslation(NS);
  const hasRunning = toolActivity.some((item) => item.status === 'running');
  const headerLabel = hasRunning ? t('tools.inProgress', { ns: NS }) : t('tools.title', { ns: NS });

  return (
    <div className="mb-2 w-full rounded-xl border border-neutral-200 bg-neutral-100/80 text-sm text-neutral-600 shadow-xs">
      <div className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-foreground/80">
        {hasRunning ? (
          <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
        ) : null}
        <span>{headerLabel}</span>
      </div>
      <ul className="space-y-1 border-t border-border/40 px-3 py-2" aria-live="polite">
        {toolActivity.map((item) => (
          <li key={item.toolName} className="flex items-center justify-between gap-2 text-xs">
            <span>{u3204FormatToolLabel(item.toolName, t)}</span>
            <span className="flex shrink-0 items-center gap-1">
              {item.status === 'running' ? (
                <>
                  <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
                  <span className="sr-only">{t('tools.inProgress', { ns: NS })}</span>
                </>
              ) : null}
              {item.status === 'success' ? (
                <>
                  <FaCheck className="h-3 w-3 text-green-600 dark:text-green-400" aria-hidden />
                  <span className="sr-only">{t('tools.status.success', { ns: NS })}</span>
                </>
              ) : null}
              {item.status === 'error' ? (
                <>
                  <FaXmark className="h-3 w-3 text-red-600 dark:text-red-400" aria-hidden />
                  <span className="sr-only">{t('tools.status.error', { ns: NS })}</span>
                </>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default U3204ToolActivityPanel;
